import React, { useEffect, useRef } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { Typography } from '@americanexpress/aqx-components';
import CarouselWithPartialNextSlide from 'src/universal/components/CarouselWithPartialNextSlide';
import LazyScrollMetricTracker from 'src/universal/components/DTM/LazyScrollMetricTracker';
import { getWinWidth, isIOS } from 'src/universal/utils/clientSideDetection';
import parseArrayToObject from 'src/universal/utils/parseArrayToObject';
import replaceStringWithVars from 'src/universal/utils/replaceStringWithVars';
import {
  sendDTMClickEvent,
  sendDTMCustomEvent
} from 'service_modules/models/dtm-tagging';
import textRemoveTags from 'service_modules/utils/text-remove-tags';
import { getCombinedG157Data } from './redux';
import styles from './__styles__/index.scss?modules';

let _id = 0;
const getInstId = () => {
  _id = _id + 1;
  return _id;
};

export const extractDtmValFromItemHeaderList = (itemHeaderList = [], index) => {
  if (!Array.isArray(itemHeaderList || itemHeaderList.length <= 0)) {
    return `item-${index}`;
  }

  const header = itemHeaderList[index];

  if (!header || typeof header !== 'string') {
    return `item-${index}`;
  }

  const strNoTags = textRemoveTags(header);
  const strNoSpecialChars = strNoTags.replace(/[^\w\s$%]/gi, '');
  return strNoSpecialChars.trim().replace(/ /g, '-');
};

const HeaderBox = ({ header, subHeader, imgUrl }) => {
  const noSubHeader = !subHeader;
  return (
    <div
      className={clsx(styles.headerBox, { [styles.noSubHeader]: noSubHeader })}
      style={{ backgroundImage: `url(${imgUrl})` }}
    >
      <Typography Component="h2" className={styles.header}>
        {header}
      </Typography>
      <Typography Component="span" className={styles.subHeader}>
        {subHeader}
      </Typography>
    </div>
  );
};

const BizValCarousel = ({
  header,
  subHeader,
  needHeaderBox,
  slideList,
  staticG157Data,
  originKey,
  itemHeaderList
}) => {
  const currRef = useRef(null);
  const impressedIndexes = useRef([]);
  const bgImgUrl = (staticG157Data?.assets || [])[0]?.url || '';
  const dtmHash = parseArrayToObject(staticG157Data?.dtmEvents || [], 'key');
  const instId = getInstId();
  const componentId =
    originKey === 'bizValueTiles' ? 'value_benefits' : 'featured_benefits';

  /*
  Note:
  (1) The following formula is used to derive the sliver width proportionaly
      according to the measurements inside this build-kit:
      https://www.figma.com/design/nSX0d0NA6ZKItUBX1F905d/EAPP-334--Horizontal-Scroll-Buildkit?node-id=1-133&p=f&m=dev
  (2) formula: (50px * dynamicWidth) / 375px
  */
  const sliverMin = 30;
  const sliderMax = 80;
  let sliverWidth = (50 * getWinWidth()) / 375;
  if (sliverWidth < sliverMin) {
    sliverWidth = sliverMin;
  } else if (sliverWidth > sliderMax) {
    sliverWidth = sliderMax;
  }

  const custObj = {
    partialSlideWidth: sliverWidth,
    initialActiveItem: 0,
    slidingSpeed: 300
  };

  const doItemImpression = index => {
    if (impressedIndexes.current.indexOf(`item${index}`) <= -1) {
      const itemVal = extractDtmValFromItemHeaderList(itemHeaderList, index);
      const dtmRaw = dtmHash['carouselImpressIndexChange'] || {};
      const eventAssetVal = replaceStringWithVars({
        varsObject: {
          componentId: componentId,
          itemName: itemVal
        },
        string: dtmRaw.eventAsset
      });

      const dtmToUse = {
        ...dtmRaw,
        eventAsset: eventAssetVal
      };

      impressedIndexes.current.push(`item${index}`);
      sendDTMCustomEvent(dtmToUse);
    }
  };

  const handleIndexChange = index => {
    doItemImpression(index);

    // detect which side the sliver is on so we style accordingly
    setTimeout(() => {
      const eleId = currRef.current?.id;
      const selector = `#${eleId} div[class*="purify_index__itemWrapper--"][class*="purify_index__isActive--"]`;
      const activeEle = document.querySelector(selector);

      if (activeEle && typeof activeEle.getBoundingClientRect === 'function') {
        const eleRect = activeEle.getBoundingClientRect();
        const container = document.querySelector(`#${eleId}`);
        if (eleRect.left > 30) {
          container.classList.add(styles.sliverOnLeft);
        } else {
          container.classList.remove(styles.sliverOnLeft);
        }
      }
    }, isIOS() ? 800 : 300);
  };

  const handleControlButtonsInView = () => {
    const dtmRaw = dtmHash['carouselImpressNavControls'] || {};
    const dtmToUse = {
      ...dtmRaw,
      eventAsset: replaceStringWithVars({
        varsObject: { componentId: componentId },
        string: dtmRaw.eventAsset
      })
    };
    sendDTMCustomEvent(dtmToUse);
  };

  const handlePaddleClick = () => {
    const dtmRaw = dtmHash['carouselArrowClick'] || {};
    const dtmToUse = {
      ...dtmRaw,
      eventAsset: replaceStringWithVars({
        varsObject: { componentId: componentId },
        string: dtmRaw.eventAsset
      })
    };
    sendDTMClickEvent(dtmToUse);
  };

  const handleDotClick = () => {
    const dtmRaw = dtmHash['carouselDotClick'] || {};
    const dtmToUse = {
      ...dtmRaw,
      eventAsset: replaceStringWithVars({
        varsObject: { componentId: componentId },
        string: dtmRaw.eventAsset
      })
    };
    sendDTMClickEvent(dtmToUse);
  };

  const handleSliverClick = () => {
    const dtmRaw = dtmHash['carouselSliverClick'] || {};
    const dtmToUse = {
      ...dtmRaw,
      eventAsset: replaceStringWithVars({
        varsObject: { componentId: componentId },
        string: dtmRaw.eventAsset
      })
    };
    sendDTMClickEvent(dtmToUse);
  };

  const handleSwipe = () => {
    const dtmRaw = dtmHash['carouselSwipe'] || {};
    const dtmToUse = {
      ...dtmRaw,
      eventAsset: replaceStringWithVars({
        varsObject: { componentId: componentId },
        string: dtmRaw.eventAsset
      })
    };
    sendDTMClickEvent(dtmToUse);
  };

  useEffect(() => {
    setTimeout(() => {
      /*
      after some investigation, the next block is a neccessary fix for an a11y issue.
      (1) Since the same html structure does not cause the issue elsewhere, so this was
          possibly a glitch on the part of AxeTool due to the fact that we are embedding
          a complex structure into yet another complex structure as we do here.
      (2) The direct setting the attribute tabindex at JSON level (or any mid process level)
          could result some negative side effects let alone the confusion factor.
      (3) So the following block is deemed to be the safest and effective approach.
      */
      const eleId = currRef.current?.id;
      const selector = `#${eleId} div[class*="flip-content-back"][style*="visibility: hidden"] a`;
      const ele = document.querySelector(selector);
      if (ele) {
        ele.setAttribute('tabindex', '-1');
      }
    }, 100);
  });

  return (
    <div
      id={`g157Carousel${instId}`}
      ref={currRef}
      className={clsx(styles.bizValTilesCarousel, styles[originKey])}
    >
      <LazyScrollMetricTracker
        callBackFunction={() => {
          doItemImpression(0);
        }}
      />
      {needHeaderBox && (
        <HeaderBox header={header} subHeader={subHeader} imgUrl={bgImgUrl} />
      )}
      {!needHeaderBox &&
        header && (
          <Typography Component="h2" className={styles.standAloneHeader}>
            {header}
          </Typography>
        )}
      <CarouselWithPartialNextSlide
        slideList={slideList}
        customization={custObj}
        onActiveIndexChange={handleIndexChange}
        onControlButtonsInView={handleControlButtonsInView}
        onPaddleClick={handlePaddleClick}
        onDotClick={handleDotClick}
        onSliverClick={handleSliverClick}
        onSwipe={handleSwipe}
      />
    </div>
  );
};

const mapStateToProps = state => {
  return {
    ...getCombinedG157Data(state)
  };
};

export default connect(mapStateToProps)(React.memo(BizValCarousel));
